import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import Trophy from "../../../common/components/Trophy/Trophy";
import type IProjectGroupsCompletionItem from "../dataTypes/IProjectGroupsCompletionItem";

interface EnduserScoreCardProps {
  parcours: IProjectGroupsCompletionItem[];
}

/**
 * Hero card displaying personal average score with gauge
 */
const EnduserScoreCard = ({ parcours }: EnduserScoreCardProps) => {
  const { t } = useTranslation();

  const { avgScore, bestScore, perfectCount, hasScorableParcours } = useMemo(() => {
    // Filter to only scorable parcours with scores
    const scorable = parcours.filter(
      (p) =>
        p.isScorable &&
        p.projectGroupMaxScore > 0 &&
        p.projectGroupScore > 0 &&
        Math.round(p.progressPercentageOnLatestBuild) > 0,
    );

    if (scorable.length === 0) {
      return {
        avgScore: null,
        bestScore: null,
        perfectCount: 0,
        hasScorableParcours: false,
      };
    }

    // Calculate average score percentage
    const avgPct =
      scorable.reduce((acc, p) => acc + (p.projectGroupScore / p.projectGroupMaxScore) * 100, 0) /
      scorable.length;

    // Find best score percentage
    const bestPct = Math.max(
      ...scorable.map((p) => (p.projectGroupScore / p.projectGroupMaxScore) * 100),
    );

    // Count perfect scores
    const perfect = parcours.filter((p) => p.hasPerfectScoreOnLatestBuild).length;

    return {
      avgScore: avgPct,
      bestScore: bestPct,
      perfectCount: perfect,
      hasScorableParcours: true,
    };
  }, [parcours]);

  // Always encouraging colors - any score is an achievement!
  const getGaugeColor = (): string => "bg-green-500";
  const getTextColor = (pct: number | null): string =>
    pct === null ? "text-gray-400" : "text-green-600";

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-medium text-gray-500">{t("enduserStats.heroCards.myScore")}</h3>
        <div className="p-2 bg-yellow-100 rounded-lg">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5 text-yellow-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
            />
          </svg>
        </div>
      </div>

      {/* Score display */}
      <div className="mb-3">
        <div className="flex items-baseline gap-2 mb-2">
          <span className={`text-3xl font-bold ${getTextColor(avgScore)}`}>
            {avgScore != null ? `${avgScore.toFixed(0)}%` : "-"}
          </span>
          <span className="text-sm text-gray-500">
            {t("enduserStats.heroCards.averageScoreLabel")}
          </span>
        </div>

        {/* Visual gauge */}
        <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
          <div
            className={`h-full ${
              avgScore != null ? getGaugeColor() : "bg-gray-300"
            } transition-all duration-500 ease-out rounded-full`}
            style={{ width: `${Math.min(avgScore ?? 0, 100)}%` }}
          />
        </div>
      </div>

      {/* No scorable message or stats */}
      {!hasScorableParcours ? (
        <div className="flex-1 flex items-center justify-center">
          <span className="text-sm text-gray-400">{t("enduserStats.noScore")}</span>
        </div>
      ) : (
        /* Secondary metrics */
        <div className="flex items-center justify-between mt-auto pt-3 border-t border-gray-100">
          <div className="flex items-center gap-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4 text-green-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
              />
            </svg>
            <span className="text-xs text-gray-500">{t("enduserStats.heroCards.bestScore")}</span>
            <span className="text-sm font-semibold text-green-600">
              {bestScore != null ? `${bestScore.toFixed(0)}%` : "-"}
            </span>
          </div>
          <div className="w-px h-4 bg-gray-200" />
          <div className="flex items-center gap-2">
            <Trophy width={16} height={16} />
            <span className="text-xs text-gray-500">
              {t("enduserStats.heroCards.perfectScores")}
            </span>
            <span className="text-sm font-semibold text-gray-900">{perfectCount}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnduserScoreCard;
